/**
 * SubscriberDistributorService
 *
 * Concrete implementation of ISubscriberDistributor
 * Topic-based message delivery with pattern matching
 *
 * Single Responsibility: Subscription management and message delivery
 */

import type {
  ISubscriberDistributor,
  MessageCallback,
  UnsubscribeFn,
  SubscriptionMetadata,
  DeliveryMetrics,
  DeliveryResult,
} from '../interfaces';
import type { IEnvelope } from '../../types';

interface Subscription {
  id: string;
  topic: string;
  callback: MessageCallback;
  isWildcard: boolean;
  isRegex: boolean;
  pattern?: RegExp;
  createdAt: Date;
  callCount: number;
  lastCallAt?: Date;
  metrics: DeliveryMetrics;
}

export class SubscriberDistributorService implements ISubscriberDistributor {
  private subscriptions: Map<string, Subscription> = new Map();
  private subscriptionCounter = 0;

  subscribe(topic: string, callback: MessageCallback): UnsubscribeFn {
    const id = `sub-${++this.subscriptionCounter}`;
    const isWildcard = topic.includes('*');
    const isRegex = topic.includes('**');

    const pattern = isRegex || isWildcard ? this.createPattern(topic) : undefined;

    const subscription: Subscription = {
      id,
      topic,
      callback,
      isWildcard,
      isRegex,
      pattern,
      createdAt: new Date(),
      callCount: 0,
      metrics: {
        subscriberId: id,
        topic,
        messageCount: 0,
        errorCount: 0,
        totalDurationMs: 0,
        averageDurationMs: 0,
      },
    };

    this.subscriptions.set(id, subscription);

    // Return unsubscribe function
    return () => {
      this.subscriptions.delete(id);
    };
  }

  async deliver(envelope: IEnvelope, topic: string): Promise<DeliveryResult> {
    const matchedSubscriptions = this.findMatchingSubscriptions(topic);
    const errors: Array<{ subscriberId: string; error: Error }> = [];
    let deliveredCount = 0;
    let totalDurationMs = 0;

    for (const sub of matchedSubscriptions) {
      const startTime = Date.now();

      try {
        await sub.callback(envelope);
        const duration = Date.now() - startTime;

        sub.callCount++;
        sub.lastCallAt = new Date();
        sub.metrics.messageCount++;
        sub.metrics.totalDurationMs += duration;
        sub.metrics.averageDurationMs = sub.metrics.totalDurationMs / sub.metrics.messageCount;
        sub.metrics.lastDeliveryTime = new Date();

        deliveredCount++;
        totalDurationMs += duration;
      } catch (error) {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        errors.push({
          subscriberId: sub.id,
          error: errorObj,
        });

        sub.metrics.errorCount++;
        sub.metrics.lastError = errorObj;
      }
    }

    return {
      success: errors.length === 0,
      matchedSubscriptions: matchedSubscriptions.length,
      deliveredCount,
      errorCount: errors.length,
      errors,
      totalDurationMs,
    };
  }

  hasSubscribers(topic: string): boolean {
    return this.findMatchingSubscriptions(topic).length > 0;
  }

  getSubscriberCount(topic: string): number {
    return this.findMatchingSubscriptions(topic).length;
  }

  getAllSubscriptions(): SubscriptionMetadata[] {
    return Array.from(this.subscriptions.values()).map(this.toMetadata);
  }

  getSubscriptions(topic: string): SubscriptionMetadata[] {
    return this.findMatchingSubscriptions(topic).map(this.toMetadata);
  }

  unsubscribeById(subscriptionId: string): boolean {
    return this.subscriptions.delete(subscriptionId);
  }

  clearTopic(topic: string): number {
    const toDelete = Array.from(this.subscriptions.entries())
      .filter(([_, sub]) => sub.topic === topic)
      .map(([id]) => id);

    toDelete.forEach((id) => this.subscriptions.delete(id));
    return toDelete.length;
  }

  clearAll(): void {
    this.subscriptions.clear();
  }

  getMetrics(subscriptionId: string): DeliveryMetrics | undefined {
    return this.subscriptions.get(subscriptionId)?.metrics;
  }

  getAllMetrics(): DeliveryMetrics[] {
    return Array.from(this.subscriptions.values()).map((sub) => sub.metrics);
  }

  getDiagnostics() {
    const subs = Array.from(this.subscriptions.values());
    const topicMap = new Map<string, number>();

    for (const sub of subs) {
      const matchingTopics = Array.from(this.subscriptions.values())
        .filter((s) => this.topicMatches(s.topic, sub.topic))
        .map((s) => s.topic);

      for (const topic of matchingTopics) {
        topicMap.set(topic, (topicMap.get(topic) ?? 0) + 1);
      }
    }

    return {
      totalSubscriptions: subs.length,
      topicCount: new Set(subs.map((s) => s.topic)).size,
      wildcardCount: subs.filter((s) => s.isWildcard).length,
      totalDeliveries: subs.reduce((sum, s) => sum + s.metrics.messageCount, 0),
      failedDeliveries: subs.reduce((sum, s) => sum + s.metrics.errorCount, 0),
      topicsBySubscriberCount: Array.from(topicMap.entries()).sort((a, b) => b[1] - a[1]),
    };
  }

  // Private methods

  private findMatchingSubscriptions(topic: string): Subscription[] {
    return Array.from(this.subscriptions.values()).filter((sub) =>
      this.topicMatches(sub.topic, topic)
    );
  }

  private topicMatches(pattern: string, actual: string): boolean {
    // Exact match
    if (pattern === actual) return true;

    // Global wildcard
    if (pattern === '**' || pattern === '*') return true;

    // Pattern matching
    if (pattern.includes('*')) {
      const regex = this.createPattern(pattern);
      return regex.test(actual);
    }

    return false;
  }

  private createPattern(topic: string): RegExp {
    // Convert glob pattern to regex
    const escaped = topic
      .split('.')
      .map((part) => {
        if (part === '**') return '.*';
        if (part === '*') return '[^.]+';
        return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      })
      .join('\\.');

    return new RegExp(`^${escaped}$`);
  }

  private toMetadata(sub: Subscription): SubscriptionMetadata {
    return {
      id: sub.id,
      topic: sub.topic,
      createdAt: sub.createdAt,
      callCount: sub.callCount,
      lastCallAt: sub.lastCallAt,
      isWildcard: sub.isWildcard,
      isRegex: sub.isRegex,
    };
  }
}
